import { studentStatus, teacherStatus } from '../../helpers/utils';
import {
  statusMarks,
  getStatus,
  UNASSIGNED as STATUS_UNASSIGNED,
  statusCodes,
  ASSIGNED,
  UNAVAILABLE,
  CORRECT,
  INCORRECT,
  UNATTEMPTED as NOTATTEMPTED
} from '../../components/status-cell/utils';
import { CHECKED, UNCHECKED } from '../../components/checkbox/constants';
import LocalStorage from '../../helpers/local-storage';

const { UNASSIGNED, UNATTEMPTED, ASSIGN_SELECTION, UNASSIGN_SELECTION } = studentStatus;
const { REVIEWED, SUBMITTED } = teacherStatus;

export const getStudents = (classrooms) => {
  if (classrooms.length > 0) {
    const classroom = classrooms[0];
    const group = classroom.groups[0];
    return group.students;
  }
  return [];
};

export const updateStudentSelectedIds = (studentAssignments, id, selectedStudentIds) => {
  let isAllSelected = true;
  // if all of the unattempted and unassigned cells for a row are selected
  // student checkbox should be checked else unchecked
  const currentStudent = studentAssignments.filter(s => s.studentUserProfileId === id);

  currentStudent.forEach((student) => {
    if ((student.studentStatus === UNATTEMPTED || student.studentStatus === UNASSIGNED) &&
      (student.teacherStatus !== REVIEWED && student.teacherStatus !== SUBMITTED)) {
      isAllSelected = false;
    }
  });

  if (isAllSelected) {
    selectedStudentIds = [...(new Set([...selectedStudentIds, id]))];
  } else {
    const index = selectedStudentIds.indexOf(id);
    if (index !== -1) {
      selectedStudentIds.splice(index, 1);
    }
  }
  return selectedStudentIds;
};

export const checkUnsavedChanges = (students) => {
  let assignSelection = 0;
  let unassignSelection = 0;

  students.forEach((student) => {
    switch (student.studentStatus) {
      case ASSIGN_SELECTION:
        assignSelection += 1;
        break;

      case UNASSIGN_SELECTION:
        unassignSelection += 1;
        break;

      default: break;
    }
  });

  if (assignSelection > 0 || unassignSelection > 0) {
    return true;
  }

  return false;
};

export const getTeacherNotes = (playLists, playListId, girlId) => {
  const playList = playLists.find(pl => pl.id === playListId);
  const teacherNotes = playList.psrs.find(pl => pl.girlId === girlId);
  teacherNotes.girlId = girlId;
  return teacherNotes;
};

export const getTopicsAndStandards = (genericCourses) => {
  if (genericCourses.length === 0) return [];

  const topicTree = [];
  const standardTopic = genericCourses.topics.filter(topic => topic.type.toLowerCase() === 'standard');
  const strandTopic = genericCourses.topics.filter(topic => topic.type.toLowerCase() === 'strand');

  if (standardTopic.length > 0) {
    standardTopic.forEach((st) => {
      st.displayName = `${st.externalKey.toUpperCase()} ${st.name}`.trim();
      st.selected = true;
    });
    topicTree.push({
      id: -1,
      displayName: 'All Standards',
      childTopics: standardTopic,
      selected: true
    });
  }

  strandTopic.forEach((strand) => {
    strand.displayName = strand.name;
    strand.selected = true;
    strand.childTopics.forEach((st) => {
      st.displayName = st.name.trim();
      st.selected = true;
      st.isExpand = !!st.childTopics.find(ct => ct.selected);
      st.childTopics.forEach((lj) => {
        lj.displayName = lj.name.trim();
        lj.selected = true;
      });
    });
    strand.isExpand = !!strand.childTopics.find(s => s.selected);
  });

  if (standardTopic.length === 0 && strandTopic.length > 0) {
    strandTopic[0].selected = true;
    strandTopic[0].childTopics[0].selected = true;
    strandTopic[0].childTopics[0].childTopics.forEach((st) => {
      st.selected = true;
    });
    strandTopic[0].isExpand = !!strandTopic[0].childTopics.find(s => s.selected);
    strandTopic[0].childTopics[0].isExpand = !!strandTopic[0].childTopics[0].childTopics.find(lj => lj.selected);
  }

  topicTree.push(...strandTopic);
  return topicTree;
};

export const getFilteredStrandAndStandards = (searchTerm, strands) => {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();
  const matchingStandards = [];
  strands[0].childTopics.forEach((st) => {
    if (st.displayName.toLowerCase().includes(lowerCaseSearchTerm)) {
      matchingStandards.push(st);
    } else {
      const matchingLearningJourneys = st.childTopics.filter(
        lj => lj.displayName.toLowerCase().includes(lowerCaseSearchTerm)
      );
      if (matchingLearningJourneys.length) {
        st.childTopics = matchingLearningJourneys;
        matchingStandards.push(st);
      }
    }
  });
  strands[0].childTopics = matchingStandards;
  return strands;
};

export const updateStrandAndStandards = (id, checked, topics) => {
  if (id) {
    if (id.indexOf('#') !== -1) {
      const topic = topics[0].childTopics.find(t => t.id.toString() === id.split('#')[0]);
      const childTopic = topic.childTopics.find(t => t.id.toString() === id.split('#')[1]);
      childTopic.selected = checked;
      topic.selected = !!topic.childTopics.find(s => s.selected);
      topic.isExpand = topic.selected;
    } else {
      const topic = topics[0].childTopics.find(t => t.id.toString() === id);
      topic.selected = checked;
      topic.childTopics.forEach((ct) => {
        ct.selected = checked;
      });
      topic.isExpand = !!topic.childTopics.find(s => s.selected);
    }
  }
  return topics;
};

export const updateStrands = (checked, topics) => {
  topics[0].childTopics.forEach((topic) => {
    topic.selected = checked;
    topic.childTopics.forEach((ct) => {
      ct.selected = checked;
    });
    topic.isExpand = !!topic.childTopics.find(s => s.selected);
  });

  return topics;
};

export const removeStandard = (id, topics) => {
  if (topics && topics[0]) {
    topics[0].childTopics.forEach((domain) => {
      if (domain.id === id) {
        domain.selected = false;
        domain.childTopics.forEach((question) => {
          question.selected = false;
        });
      }
    });
  }
  return topics;
};

export const updateCount = (id, topics = [], studentAssignments = []) => {
  let selectedTopic = [];
  let LJIds = [];
  topics.forEach((topic) => {
    topic.childTopics.forEach((ct) => {
      if (ct.id === id) {
        selectedTopic = ct;
      }
    });
  });

  LJIds = selectedTopic && selectedTopic.childTopics && selectedTopic.childTopics.map(ct => ct.id);
  return studentAssignments.filter(assignment =>
    !LJIds.includes(assignment.learningJourneyId));
};

export const getAllLearningJourneys = (selectedTopics, ignoreSelected) => {
  const learningJourneyIds = [];
  selectedTopics.forEach((strands) => {
    strands.childTopics.forEach((standards) => {
      if (standards.selected || ignoreSelected) {
        standards.childTopics.forEach((item) => {
          if (item.selected || ignoreSelected) {
            learningJourneyIds.push(item.id);
          }
        });
      }
    });
  });
  return learningJourneyIds;
};

export const updateGridCells = (studentId, assignmentStatuses,
  studentAssignments, status, clonedSelectedLJIDs, clonedStudentIds, learningJourneyIds, studentUnAssignments) => {
  let filteredStudentAssignments = [];
  const filteredAssignments = assignmentStatuses.filter(a => a.studentProfileId === studentId);

  const isAssignedItem = item => (item.studentStatus !== statusCodes[STATUS_UNASSIGNED]) &&
    (item.studentStatus !== statusCodes[UNAVAILABLE]);

  const hasAssignments = filteredAssignments.some(item => isAssignedItem(item));

  if (hasAssignments) {
    studentUnAssignments.push({
      profileId: studentId,
      assignmentId: filteredAssignments.find(item => isAssignedItem(item)).assignmentId
    });
  } else {
    studentAssignments.push({
      profileId: studentId,
      questions: learningJourneyIds
    });
  }
  filteredAssignments.forEach((item) => {
    if (status === CHECKED) {
      if (item.studentStatus === UNASSIGNED || item.studentStatus === statusCodes[ASSIGNED]) {
        item.studentStatus = item.studentStatus === UNASSIGNED ? ASSIGN_SELECTION : UNASSIGN_SELECTION;
      }
    } else if (status === UNCHECKED) {
      if (item.studentStatus === ASSIGN_SELECTION || item.studentStatus === UNASSIGN_SELECTION) {
        item.studentStatus = item.studentStatus === ASSIGN_SELECTION ? UNASSIGNED : statusCodes[ASSIGNED];
      }
    }
  });

  if (status !== CHECKED) {
    if (studentAssignments.length > 0) {
      filteredStudentAssignments = studentAssignments.filter(a => a.profileId !== studentId);
      studentAssignments = filteredStudentAssignments;
    }
    if (studentUnAssignments.length > 0) {
      filteredStudentAssignments = studentUnAssignments.filter(a => a.profileId !== studentId);
      studentUnAssignments = filteredStudentAssignments;
    }
    clonedStudentIds.splice(clonedStudentIds.indexOf(studentId), 1);
  } else {
    clonedStudentIds.push(studentId);
  }

  return { assignmentStatuses, studentAssignments, clonedSelectedLJIDs, clonedStudentIds, studentUnAssignments };
};

export const filterStandardByLJCount = (selectedTopics = [], LJCount = 0) => {
  if (selectedTopics.length === 0) return [];
  let count = 0;
  let standardwithLJCount = 0;

  selectedTopics.forEach((topic) => {
    if (topic && topic.childTopics) {
      topic.childTopics.forEach((childTopic) => {
        count += (childTopic && childTopic.childTopics && childTopic.childTopics.length) || 0;
        if (childTopic.childTopics.length) {
          standardwithLJCount += 1;
        }
        if (standardwithLJCount > 1) {
          childTopic.selected = !(count > LJCount);
          if (!childTopic.selected) topic.selected = false;
        }
      });
    }
  });
  return selectedTopics;
};


export const getSelectedTopicCount = (topics) => {
  let selectedTopicCount = 0;
  let topicCount = 0;

  topics.forEach((val) => {
    val.childTopics.forEach((st) => {
      topicCount += 1;
      if (st.selected) {
        selectedTopicCount += 1;
      }
    });
  });

  return { selectedTopicCount, topicCount };
};

export const getSelectedQuestions = (selectedTopics) => {
  const questions = [];
  selectedTopics.forEach((topic) => {
    topic.childTopics.forEach((domain) => {
      if (domain.selected) {
        domain.childTopics.forEach((question) => {
          if (question.selected) {
            questions.push(question);
          }
        });
      }
    });
  });
  return questions;
};

const getSelectedCurriculum = (countryCode) => {
  const LAST_USED_PUBLICATION = 'LAST_USED_PUBLICATION';
  const LAST_PUBLICATION_KEY = `${countryCode}_${LAST_USED_PUBLICATION}`;
  return LocalStorage.getItem(LAST_PUBLICATION_KEY).label;
};

const getCSV = ({ selectedTopics, assignments, students, countryCode, assessmentType, i18n }) => {
  const delimiter = ',';
  const newLine = '\r\n';

  const selectedAssignment = selectedTopics[0];
  let csvData = newLine;
  csvData += `${getSelectedCurriculum(countryCode)}${newLine}`;
  csvData += `${selectedAssignment.assessmentName}${newLine}`;
  csvData += `${assessmentType}${newLine}`;
  csvData += newLine;

  const selectedQuestions = getSelectedQuestions(selectedTopics);
  let domainHeader = delimiter + delimiter + delimiter;
  let questionHeader = delimiter + delimiter + delimiter;

  let correct = 0;
  let assigned = 0;
  let incorrect = 0;
  let attempted = 0;
  let correctPct = 0;
  let completedPct = 0;

  const statusCalulations = (assignmentStatus) => {
    switch (getStatus(assignmentStatus)) {
      case ASSIGNED: {
        assigned += 1;
        break;
      }

      case CORRECT: {
        correct += 1;
        assigned += 1;
        attempted += 1;
        break;
      }

      case NOTATTEMPTED: {
        incorrect += 1;
        assigned += 1;
        break;
      }

      case INCORRECT: {
        incorrect += 1;
        assigned += 1;
        attempted += 1;
        break;
      }

      default: {
        break;
      }
    }
  };

  const pctCalculations = () => {
    if ((correct + incorrect) !== 0) {
      correctPct = Math.round((100 * correct) / (correct + incorrect));
    }
    if (assigned !== 0) {
      completedPct = Math.round((100 * attempted) / assigned);
    }
  };

  let correctData = '';
  let completedData = '';
  let correctTotal = 0;
  let assignedTotal = 0;
  let incorrectTotal = 0;
  let attemptedToatal = 0;
  selectedQuestions.forEach((question) => {
    domainHeader += `${question.domain}${delimiter}`;
    questionHeader += `${question.name}${delimiter}`;
    correctPct = 0;
    completedPct = 0;
    if (students && students.length) {
      correct = 0;
      assigned = 0;
      incorrect = 0;
      attempted = 0;
      students.forEach((student) => {
        const assignmentStatus = assignments.find(assignment =>
          assignment.learningJourneyId === question.id &&
          assignment.studentProfileId === student.id).studentStatus;

        statusCalulations(assignmentStatus);
      });

      correctTotal += correct;
      assignedTotal += assigned;
      incorrectTotal += incorrect;
      attemptedToatal += attempted;

      pctCalculations();
    }
    correctData += `${correctPct}${delimiter}`;
    completedData += `${completedPct}${delimiter}`;
  });
  correctData = `${correctData.slice(0, -1)}${newLine}`;
  completedData = `${completedData.slice(0, -1)}${newLine}`;

  let correctPctTotal = 0;
  let completedPctTotal = 0;
  if ((incorrectTotal + correctTotal) !== 0) {
    correctPctTotal = Math.round((100 * correctTotal) / (incorrectTotal + correctTotal));
  }
  if (assignedTotal !== 0) {
    completedPctTotal = Math.round((100 * attemptedToatal) / assignedTotal);
  }

  csvData += `${domainHeader.slice(0, -1)}${newLine}${questionHeader.slice(0, -1)}${newLine}`;
  csvData += `${i18n.t`Students:`}${delimiter}${i18n.t`% Correct`}${delimiter}${i18n.t`% Completed`}${newLine}`;

  if (students && students.length) {
    students.forEach((student) => {
      csvData += `${student.firstName} ${student.lastName}${delimiter}`;

      correctPct = 0;
      completedPct = 0;
      correct = 0;
      assigned = 0;
      incorrect = 0;
      attempted = 0;
      let marks = '';
      selectedQuestions.forEach((question) => {
        const assignmentStatus = assignments.find(assignment =>
          assignment.learningJourneyId === question.id &&
          assignment.studentProfileId === student.id).studentStatus;

        statusCalulations(assignmentStatus);

        marks += `${statusMarks[getStatus(assignmentStatus)]}${delimiter}`;
      });

      pctCalculations();

      csvData += `${correctPct}${delimiter}${completedPct}${delimiter}`;
      csvData += marks;

      csvData = `${csvData.slice(0, -1)}${newLine}`;
    });
  }

  csvData += `${newLine}`;
  csvData += `${i18n.t`% Class Correct:`}${delimiter}${correctPctTotal}${delimiter}${delimiter}${correctData}`;
  // eslint-disable-next-line max-len
  csvData += `${i18n.t`% Class Completed:`}${delimiter}${completedPctTotal}${delimiter}${delimiter}${completedData}`;

  csvData += `${delimiter}${i18n.t`Legend`}${newLine}`;
  csvData += `${delimiter}-${delimiter}${i18n.t`"not assigned, scheduled, currently unavailable"`}${newLine}`;
  csvData += `${delimiter}${delimiter}${i18n.t`assigned`}${newLine}`;
  csvData += `${delimiter}0${delimiter}${i18n.t`"incorrect answer, not attempted"`}${newLine}`;
  csvData += `${delimiter}1${delimiter}${i18n.t`correct answer`}${newLine}`;
  // eslint-disable-next-line max-len
  csvData += `${delimiter}${i18n.t`% Correct`}${delimiter}${i18n.t`percentage of correctly answered teacher-assigned questions by student or class`}${newLine}`;
  // eslint-disable-next-line max-len
  csvData += `${delimiter}${i18n.t`% Completed`}${delimiter}${i18n.t`overall percentage of teacher-assigned questions attempted by student or class`}`;

  return csvData;
};

const downloadCSV = (csvData, exportFileName) => {
  try {
    const exportedFileName = `${exportFileName}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, exportedFileName);
    } else {
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', exportedFileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  } catch (err) {
    console.log(err); // eslint-disable-line no-console
  }
};

export const exportAssignmentsAsCSV = ({
  selectedTopics,
  assignments,
  students,
  countryCode,
  exportFileName,
  assessmentType,
  i18n
}) => {
  const csvData = getCSV({ selectedTopics, assignments, students, countryCode, assessmentType, i18n });
  downloadCSV(csvData, exportFileName);
};
